import { FiltersGroupProps } from "@shared/features/filters/types";
import FiltersSection from "common/Filters/FiltersChips/FiltersSection";
import { useSelector } from "react-redux";
import useMedia from "utils/hooks/useMedia";
import styled, { useTheme } from "styled-components";
import SlidersOutlined from "@ant-design/icons/SlidersOutlined";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import React, { CSSProperties, useContext, useMemo, useState } from "react";
import useFiltersQuery from "common/Filters/common/useFiltersQuery";
import FiltersCounter from "common/Filters/common/FiltersCounter";
import { SINGLE_FILTERS_ROW_BREAKPOINT } from "@shared/features/filters/constants";
import {
  FilterChipsWrapper,
  getNewLookChipColors,
} from "common/Filters/common/styles";
import LookIcon from "common/Icons/LookIcon";
import { SlidersHorizontal } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { FILTERS_DRAWER_BUTTON_ID } from "testIds";
import { useBottomNavigator } from "utils/hooks/misc";
import Drawer from "common/Drawers/Drawer.tsx";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

const FiltersChips = ({
  filtersSelectors,
  filtersActions,
  filterTypeToTitle,
  hideFilterTypes,
  extraComponent,
  style,
  isSingleRowView: propsIsSingleRowView,
  singleRowStyle,
  hideFiltersButton,
  hideFilters,
}: FiltersGroupProps & {
  style?: CSSProperties;
  isSingleRowView?: boolean;
  singleRowStyle?: CSSProperties;
  hideFiltersButton?: boolean;
  hideFilters?: boolean;
}) => {
  const { i18n } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const isSingleRowViewMedia = useMedia(
    [`(max-width: ${SINGLE_FILTERS_ROW_BREAKPOINT})`],
    [true],
    false
  );
  const isSingleRowView = propsIsSingleRowView || isSingleRowViewMedia;
  const maxInRow = useMedia(
    [`(max-width: 800px)`, `(max-width: 1100px)`, `(max-width: 1250px)`],
    [3, 4, 5],
    6
  );
  const filtersConfig = useSelector(filtersSelectors.selectFiltersConfig);
  const filtersData = useSelector(filtersSelectors.selectFiltersData);
  const numActiveFilters = useSelector(filtersSelectors.selectNumActiveFilters);
  useFiltersQuery(filtersSelectors, filtersActions);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isPageTabs } = useContext(PageTabsContext) || {};
  const { isNewLook } = useTheme();

  const filterTypes = useMemo(() => {
    return filtersConfig.filterTypes?.filter(
      (filterType) =>
        filterType in filtersData && !hideFilterTypes?.includes(filterType)
    );
  }, [filtersConfig.filterTypes, hideFilterTypes, filtersData]);

  const content = useMemo(() => {
    return (
      !hideFilters &&
      filterTypes?.map((filterType) => {
        return (
          <FiltersSection
            filterType={filterType}
            filtersSelectors={filtersSelectors}
            filtersActions={filtersActions}
            key={filterType}
            isSingleRowView={isSingleRowView}
            filterTypeToTitle={filterTypeToTitle}
          />
        );
      })
    );
  }, [
    filterTypes,
    filtersSelectors,
    filtersActions,
    isSingleRowView,
    filterTypeToTitle,
    hideFilters,
    i18n.language,
  ]);

  // New look: an empty chips bar only draws a stray pair of hairlines.
  if (isNewLook && !extraComponent && (hideFilters || !filterTypes?.length))
    return null;

  if (isSingleRowView) {
    return (
      <FilterChipsWrapper
        style={style}
        $showingBottomNavigator={isBottomNavigator}
        $addBorderTop={isBottomNavigator && !isPageTabs}
      >
        <SingleRowViewWrapper style={singleRowStyle}>
          {!hideFiltersButton && !hideFilters && (
            <ButtonWrapper>
              {!!filterTypes?.length && (
                <OpenDrawerButton
                  onClick={() => setFiltersDrawerOpen(true)}
                  data-testid={FILTERS_DRAWER_BUTTON_ID}
                >
                  <LookIcon
                    classic={<SlidersOutlined />}
                    newLook={
                      <SlidersHorizontal
                        size={16}
                        strokeWidth={NEW_LOOK_ICON_STROKE}
                      />
                    }
                  />
                </OpenDrawerButton>
              )}
              <FiltersCounter
                numActiveFilters={numActiveFilters}
                isIconButton={true}
              />
            </ButtonWrapper>
          )}
          {extraComponent}
          {content}
          {!hideFilters && (
            <Drawer
              open={filtersDrawerOpen}
              size={300}
              closable={false}
              onClose={() => setFiltersDrawerOpen(false)}
              styles={DRAWER_STYLES}
              destroyOnHidden={false}
            >
              <FiltersColumn
                filtersActions={filtersActions}
                filtersSelectors={filtersSelectors}
                filterTypeToTitle={filterTypeToTitle}
                hideFilterTypes={hideFilterTypes}
              />
            </Drawer>
          )}
        </SingleRowViewWrapper>
      </FilterChipsWrapper>
    );
  }

  const numFilters = Object.keys(filtersData).length;
  let numItemsInRow = Math.ceil(numFilters / 2);
  if (numFilters <= maxInRow) {
    numItemsInRow = numFilters;
  }

  return (
    <FilterChipsWrapper style={style}>
      <Wrapper
        $numColumns={
          maxInRow && numItemsInRow > maxInRow ? maxInRow : numItemsInRow
        }
      >
        {content}
      </Wrapper>
    </FilterChipsWrapper>
  );
};

export default React.memo(FiltersChips);

const DRAWER_STYLES = { body: { padding: 16 } };

const Wrapper = styled.div<{ $numColumns?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(p) => p.$numColumns},
    minmax(max-content, 1fr)
  );
  grid-gap: 12px 16px;
`;
const SingleRowViewWrapper = styled.div`
  display: flex;
  column-gap: 8px;
`;
const OpenDrawerButton = styled.div`
  background-color: ${(p) => p.theme.colors.filterChipsColor};
  padding: 0 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  height: 32px;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.primaryText};
  cursor: pointer;
  font-size: 20px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    width: 40px;
    justify-content: center;
    padding: 0;
    border-radius: ${p.theme.radii.control}px;
    background: ${getNewLookChipColors(p.theme).background};
    border: ${getNewLookChipColors(p.theme).border};
    color: ${p.theme.colors.titleColor};
  `}
`;
const ButtonWrapper = styled.div`
  position: relative;
`;
